import { useEffect, useRef, useState } from 'react';
import { ChessRules, toUci, type Move } from '../domain/chess/ChessRules';
import type { Session } from '../domain/puzzle/PuzzleSessionController';

export function puzzleMoves(session: Session) {
  const rules = new ChessRules(session.puzzle.fenBefore);
  const moves = [rules.play(session.puzzle.setupMove)];
  for (const [i, uci] of session.puzzle.solutionMoves.slice(0, session.index).entries()) {
    // A mate-in-one can have another accepted mating move. Show the move actually played.
    const actual =
      session.solved &&
      !session.continuation &&
      i === session.index - 1 &&
      session.lastMove?.before === rules.fen
        ? toUci(session.lastMove)
        : uci;
    moves.push(rules.play(actual));
  }
  if (!session.solved && session.mistakes.length) {
    const mistake = session.mistakes.at(-1)!;
    // Retry restores the exact stored line; only show a branch while the board is on it.
    if (session.fen !== rules.fen && mistake.fenBefore === rules.fen) {
      moves.push(rules.play(mistake.uci));
      for (const uci of session.refutation?.moves.slice(0, session.refutation.index) ?? [])
        moves.push(rules.play(uci));
    }
  }
  return moves;
}

/** A display-only continuation trail; the controller remains the source of chess state. */
export function useScoresheet(session: Session) {
  const [trail, setTrail] = useState<{ key: string; moves: Move[] }>({ key: '', moves: [] });
  const key = session.attemptId ?? `${session.puzzle.id}:${session.startedAt}`;
  useEffect(() => {
    const move = session.lastMove;
    if (!session.continuation || !move) return;
    setTrail((previous) => {
      const moves = previous.key === key ? previous.moves : [];
      if (moves.at(-1)?.after === move.after) return previous;
      const base = puzzleMoves(session).at(-1)!.after;
      if (move.after === base) return previous;
      return { key, moves: [...moves, move] };
    });
  }, [key, session]);
  return [
    ...puzzleMoves(session),
    ...(session.continuation && trail.key === key ? trail.moves : []),
  ];
}

export function Scoresheet({ moves, lastMove }: { moves: Move[]; lastMove?: Move }) {
  const current = useRef<HTMLTableCellElement>(null);
  const scroll = useRef<HTMLDivElement>(null);
  const rows = new Map<number, { w?: Move; b?: Move }>();
  for (const move of moves) {
    const number = Number(move.before.split(' ')[5]);
    rows.set(number, { ...rows.get(number), [move.color]: move });
  }
  useEffect(() => {
    const cell = current.current,
      container = scroll.current;
    if (!cell || !container) return;
    const bounds = cell.getBoundingClientRect(),
      viewport = container.getBoundingClientRect();
    if (bounds.bottom > viewport.bottom) container.scrollTop += bounds.bottom - viewport.bottom;
    else if (bounds.top < viewport.top) container.scrollTop -= viewport.top - bounds.top;
  }, [lastMove]);
  return (
    <section className="scoresheet" aria-label="Scoresheet">
      <h2>Scoresheet</h2>
      <div
        className="score-scroll"
        ref={scroll}
        tabIndex={0}
        role="region"
        aria-label="Moves played"
      >
        <table>
          <thead className="sr-only">
            <tr>
              <th>Move</th>
              <th>White</th>
              <th>Black</th>
            </tr>
          </thead>
          <tbody>
            {[...rows].map(([number, row]) => (
              <tr key={number}>
                <th scope="row">{number}.</th>
                {(['w', 'b'] as const).map((color) => {
                  const move = row[color];
                  const active = !!move && move.after === lastMove?.after;
                  return (
                    <td
                      key={color}
                      ref={active ? current : undefined}
                      aria-current={active ? 'step' : undefined}
                      data-testid={active ? 'last-move' : undefined}
                    >
                      {move?.san ?? '…'}
                      {active && (
                        <span className="score-route">
                          {move.from} → {move.to}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
